import { useAppSelector } from "../../app";
import { useCallback, useMemo } from "react";
import {
  CategorizationAnnotation,
  ICategorization,
} from "../../configMap/interfaces/category";
import selectCategorizationAnnotationId from "../selectors/selectCategorizationAnnotationId";
import selectCategorizations from "../selectors/selectCategorizations";
import { getInLanguageOrDefault } from "../../common/utilities/language";
import { useTranslation } from "react-i18next";
import selectIsCategorizationInSideControls from "../selectors/selectIsCategorizationInSideControls";
import { KeyByAny } from "../../documentSet/interfaces/overview";

const useParentCategorization = (
  categoryAnnotation: CategorizationAnnotation | null,
  categorization: ICategorization,
  documentId: string | undefined,
  groupBlockId: string | undefined,
  index: number,
  attribute: KeyByAny | undefined,
) => {
  const { t } = useTranslation("annotationSideControls", {
    keyPrefix: "linkedParentCategorization",
  });

  // Optional all the way down. A categorization without a search config has no
  // parent to link to, and reading through the missing config took the whole
  // detail page down rather than the one row that had nothing to show.
  const parentCategorization = categorization.search?.parentCategorization;

  const isParentInSideControls = useAppSelector((state) =>
    selectIsCategorizationInSideControls(state, {
      categorizationId: parentCategorization?.id,
    }),
  );

  const parentCategoryAnnotation = useAppSelector((state) =>
    selectCategorizationAnnotationId(state, {
      documentId: documentId,
      categorizationId: parentCategorization?.id,
      groupBlockId: groupBlockId,
      index: isParentInSideControls ? 1 : index,
    }),
  );

  const categorizations = useAppSelector((state) =>
    selectCategorizations(state, { withConfig: false }),
  ) as Array<ICategorization>;

  const {
    i18n: { language },
  } = useTranslation();

  const parentCategorizationValue = useMemo(() => {
    const parentCategoryValue = parentCategoryAnnotation?.value;
    if (!parentCategoryValue) {
      return null;
    }

    const { separator = null, index = null } =
      parentCategorization?.valueExtractor || {};
    if (!separator || index === null) {
      return parentCategoryValue.trim();
    }

    const splittedValue = parentCategoryValue.split(separator);
    if (splittedValue.length <= index) {
      return null;
    }

    return splittedValue[index].trim();
  }, [parentCategorization, parentCategoryAnnotation]);

  const isCategorizationDisabledByParent = useMemo(() => {
    return (
      parentCategorization?.disableIfNoParentValue &&
      !parentCategoryAnnotation?.externalDataId &&
      !categoryAnnotation
    );
  }, [
    parentCategoryAnnotation?.externalDataId,
    parentCategorization?.disableIfNoParentValue,
    categoryAnnotation,
  ]);

  const linkedParentLabel = useMemo(() => {
    if (!parentCategorization) {
      return null;
    }

    const parent = categorizations.find(
      (c) => c.id === parentCategorization.id,
    );
    if (!parent) {
      return null;
    }

    return getInLanguageOrDefault(parent.label, language);
  }, [parentCategorization, categorizations, language]);

  // Plain primitive passthrough — memoizing it bought nothing (a string/null
  // has no identity to preserve), and the memo blocked the compiler because it
  // cannot express an optional-chained dependency.
  const parentCategorizationType = parentCategorization?.type || null;

  const disabledParentTooltip = useMemo(() => {
    return t("disabledByNoParentValue").replace(
      "$PARENT",
      linkedParentLabel?.toUpperCase() ?? "",
    );
  }, [linkedParentLabel, t]);

  const getAttributeFilter = useCallback(
    (searchValue?: string): string | undefined => {
      const isSelfReferencing = parentCategorization?.id === categorization.id;
      const hasSearchValue = searchValue && searchValue.length > 0;

      const { normalizationEndpoint, ...filteredAttribute } = attribute || {};
      const hasFilteredAttribute = Object.keys(filteredAttribute).length > 0;

      // When self-referencing with a search value, don't apply parent filter
      if (isSelfReferencing && hasSearchValue) {
        if (hasFilteredAttribute) {
          return JSON.stringify(filteredAttribute);
        }
        return undefined;
      }

      if (parentCategorizationType && parentCategorizationValue) {
        const filter: KeyByAny = {
          ...filteredAttribute,
          [parentCategorizationType]: [parentCategorizationValue],
        };
        return JSON.stringify(filter);
      }

      if (hasFilteredAttribute) {
        return JSON.stringify(filteredAttribute);
      }

      return undefined;
    },
    [
      attribute,
      parentCategorizationType,
      parentCategorizationValue,
      parentCategorization?.id,
      categorization.id,
    ],
  );

  return {
    isCategorizationDisabledByParent,
    disabledParentTooltip,
    getAttributeFilter,
  };
};

export default useParentCategorization;
